// Chemin: C:\gestion-copro\src\services\copropriete.service.ts
// Session 81 : ajout initialisation automatique quota SENTINELLE
// Session 88 : migration administrateur → coordinateur (double écriture Firestore)

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
  serverTimestamp,
  UpdateData
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Copropriete,
  CoproprieteCreateData,
  CoproprieteUpdateData,
  getFormuleSentinelle,
  getQuotaSentinelle
} from '../types/copropriete.types';

const COLLECTION_NAME = 'coproprietes';

/**
 * Calcule les dates d'expiration des garanties à partir de la date de réception
 */
function calculerDatesGaranties(dateReception: Date): {
  dateExpirationGPA: Date;
  dateExpirationBiennale: Date;
  dateExpirationDecennale: Date;
} {
  const gpa = new Date(dateReception);
  gpa.setFullYear(gpa.getFullYear() + 1);

  const biennale = new Date(dateReception);
  biennale.setFullYear(biennale.getFullYear() + 2);

  const decennale = new Date(dateReception);
  decennale.setFullYear(decennale.getFullYear() + 10);

  return {
    dateExpirationGPA: gpa,
    dateExpirationBiennale: biennale,
    dateExpirationDecennale: decennale
  };
}

/**
 * Calcule la date de renouvellement du quota (1 an après début abonnement)
 * Session 81
 */
function calculerDateRenouvellementQuota(dateDebutAbonnement: Date): Date {
  const renouvellement = new Date(dateDebutAbonnement);
  renouvellement.setFullYear(renouvellement.getFullYear() + 1);
  return renouvellement;
}

/**
 * Récupère une copropriété par son ID
 */
export async function getCopropriete(coproprieteId: string): Promise<Copropriete | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, coproprieteId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Copropriete;
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération de la copropriété:', error);
    throw error;
  }
}

/**
 * Crée une nouvelle copropriété
 * Note: En production, cette fonction devrait être appelée via Cloud Functions
 * pour des raisons de sécurité (création réservée au système)
 * 
 * Session 81 : Initialisation automatique du quota SENTINELLE
 * Session 88 : coordinateurId/Nom remplacent administrateurId/Nom
 *              Double écriture Firestore pour rétrocompatibilité
 */
export async function createCopropriete(
  coproprieteId: string,
  data: CoproprieteCreateData,
  createdBy: string
): Promise<Copropriete> {
  try {
    // Session 81 : Calculer formule et quota SENTINELLE selon le nombre de lots
    const sentinelleFormule = getFormuleSentinelle(data.nombreLots);
    const sentinelleQuota = getQuotaSentinelle(data.nombreLots);
    const sentinelleRenouvellement = calculerDateRenouvellementQuota(data.abonnement.dateDebut);

    // Préparer les données de base
    const coproprieteData: Omit<Copropriete, 'id'> = {
      nom: data.nom,
      adresse: data.adresse,
      nombreLots: data.nombreLots,
      tantiemesTotal: data.tantiemesTotal,
      typeConstruction: data.typeConstruction,
      abonnement: {
        plan: data.abonnement.plan,
        dateDebut: Timestamp.fromDate(data.abonnement.dateDebut),
        dateFin: Timestamp.fromDate(data.abonnement.dateFin),
        statut: data.abonnement.statut
      },
      // Session 81 : Initialisation quota SENTINELLE
      sentinelleFormule,
      sentinelleQuota,
      sentinelleUsage: 0,
      sentinelleRenouvellement: Timestamp.fromDate(sentinelleRenouvellement),
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      createdBy
    };

    // Si date de réception fournie, calculer les garanties
    if (data.dateReceptionTravaux) {
      coproprieteData.dateReceptionTravaux = Timestamp.fromDate(data.dateReceptionTravaux);
      
      const garanties = calculerDatesGaranties(data.dateReceptionTravaux);
      coproprieteData.dateExpirationGPA = Timestamp.fromDate(garanties.dateExpirationGPA);
      coproprieteData.dateExpirationBiennale = Timestamp.fromDate(garanties.dateExpirationBiennale);
      coproprieteData.dateExpirationDecennale = Timestamp.fromDate(garanties.dateExpirationDecennale);
    }

    // Champs optionnels identité visuelle
    if (data.sceauUrl) {
      coproprieteData.sceauUrl = data.sceauUrl;
    }
    if (data.slug) {
      coproprieteData.slug = data.slug;
    }

    // Champs optionnels onboarding
    if (data.statutOnboarding) {
      coproprieteData.statutOnboarding = data.statutOnboarding;
    }
    if (data.typeOffre) {
      coproprieteData.typeOffre = data.typeOffre;
    }

    // Session 88 : Coordinateur (nouveaux champs + rétrocompat anciens champs)
    if (data.coordinateurId) {
      coproprieteData.coordinateurId = data.coordinateurId;
      coproprieteData.administrateurId = data.coordinateurId; // Rétrocompat
    }
    if (data.coordinateurNom) {
      coproprieteData.coordinateurNom = data.coordinateurNom;
      coproprieteData.administrateurNom = data.coordinateurNom; // Rétrocompat
    }
    if (data.coordinateurEstMembreCS !== undefined) {
      coproprieteData.coordinateurEstMembreCS = data.coordinateurEstMembreCS;
    }

    const docRef = doc(db, COLLECTION_NAME, coproprieteId);
    await setDoc(docRef, coproprieteData);

    return {
      id: coproprieteId,
      ...coproprieteData
    } as Copropriete;
  } catch (error) {
    console.error('Erreur lors de la création de la copropriété:', error);
    throw error;
  }
}

/**
 * Met à jour une copropriété existante
 * 
 * Session 81 : Si nombreLots change, recalculer formule et quota SENTINELLE
 * Session 88 : coordinateur remplace administrateur (double écriture)
 */
export async function updateCopropriete(
  coproprieteId: string,
  data: CoproprieteUpdateData
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, coproprieteId);
    
    // Construire l'objet de mise à jour
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateData: UpdateData<any> = {
      updatedAt: serverTimestamp()
    };

    // Champs simples
    if (data.nom !== undefined) updateData.nom = data.nom;
    if (data.tantiemesTotal !== undefined) updateData.tantiemesTotal = data.tantiemesTotal;
    if (data.typeConstruction !== undefined) updateData.typeConstruction = data.typeConstruction;

    // Session 81 : Si nombreLots change, recalculer formule et quota
    if (data.nombreLots !== undefined) {
      updateData.nombreLots = data.nombreLots;
      
      // Recalculer la formule et le quota
      const nouvelleFormule = getFormuleSentinelle(data.nombreLots);
      const nouveauQuota = getQuotaSentinelle(data.nombreLots);
      
      updateData.sentinelleFormule = nouvelleFormule;
      updateData.sentinelleQuota = nouveauQuota;
    }

    // Adresse (mise à jour partielle possible)
    if (data.adresse) {
      if (data.adresse.rue !== undefined) updateData['adresse.rue'] = data.adresse.rue;
      if (data.adresse.codePostal !== undefined) updateData['adresse.codePostal'] = data.adresse.codePostal;
      if (data.adresse.ville !== undefined) updateData['adresse.ville'] = data.adresse.ville;
    }

    // Date de réception et recalcul des garanties
    if (data.dateReceptionTravaux !== undefined) {
      updateData.dateReceptionTravaux = Timestamp.fromDate(data.dateReceptionTravaux);
      
      const garanties = calculerDatesGaranties(data.dateReceptionTravaux);
      updateData.dateExpirationGPA = Timestamp.fromDate(garanties.dateExpirationGPA);
      updateData.dateExpirationBiennale = Timestamp.fromDate(garanties.dateExpirationBiennale);
      updateData.dateExpirationDecennale = Timestamp.fromDate(garanties.dateExpirationDecennale);
    }

    // Dates de garantie manuelles (écrase le calcul auto si fournies)
    if (data.dateExpirationGPA !== undefined) {
      updateData.dateExpirationGPA = Timestamp.fromDate(data.dateExpirationGPA);
    }
    if (data.dateExpirationBiennale !== undefined) {
      updateData.dateExpirationBiennale = Timestamp.fromDate(data.dateExpirationBiennale);
    }
    if (data.dateExpirationDecennale !== undefined) {
      updateData.dateExpirationDecennale = Timestamp.fromDate(data.dateExpirationDecennale);
    }

    // Abonnement (mise à jour partielle possible)
    if (data.abonnement) {
      if (data.abonnement.plan !== undefined) updateData['abonnement.plan'] = data.abonnement.plan;
      if (data.abonnement.statut !== undefined) updateData['abonnement.statut'] = data.abonnement.statut;
      if (data.abonnement.dateDebut !== undefined) {
        updateData['abonnement.dateDebut'] = Timestamp.fromDate(data.abonnement.dateDebut as unknown as Date);
      }
      if (data.abonnement.dateFin !== undefined) {
        updateData['abonnement.dateFin'] = Timestamp.fromDate(data.abonnement.dateFin as unknown as Date);
      }
    }

    // Identité visuelle
    if (data.sceauUrl !== undefined) updateData.sceauUrl = data.sceauUrl;
    if (data.slug !== undefined) updateData.slug = data.slug;

    // Onboarding
    if (data.statutOnboarding !== undefined) updateData.statutOnboarding = data.statutOnboarding;
    if (data.typeOffre !== undefined) updateData.typeOffre = data.typeOffre;

    // Session 88 : Coordinateur (nouveaux champs + rétrocompat anciens champs)
    if (data.coordinateurId !== undefined) {
      updateData.coordinateurId = data.coordinateurId;
      updateData.administrateurId = data.coordinateurId; // Rétrocompat
    }
    if (data.coordinateurNom !== undefined) {
      updateData.coordinateurNom = data.coordinateurNom;
      updateData.administrateurNom = data.coordinateurNom; // Rétrocompat
    }
    if (data.coordinateurEstMembreCS !== undefined) {
      updateData.coordinateurEstMembreCS = data.coordinateurEstMembreCS;
    }
    if (data.dateDesignationCoordinateur !== undefined) {
      updateData.dateDesignationCoordinateur = Timestamp.fromDate(data.dateDesignationCoordinateur);
      updateData.dateDesignationAdmin = Timestamp.fromDate(data.dateDesignationCoordinateur); // Rétrocompat
    }
    if (data.mandatActifId !== undefined) {
      updateData.mandatActifId = data.mandatActifId;
    }

    // Session 81 : Quota SENTINELLE (mise à jour manuelle si nécessaire)
    if (data.sentinelleFormule !== undefined) updateData.sentinelleFormule = data.sentinelleFormule;
    if (data.sentinelleQuota !== undefined) updateData.sentinelleQuota = data.sentinelleQuota;
    if (data.sentinelleUsage !== undefined) updateData.sentinelleUsage = data.sentinelleUsage;
    if (data.sentinelleRenouvellement !== undefined) {
      updateData.sentinelleRenouvellement = Timestamp.fromDate(data.sentinelleRenouvellement);
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la copropriété:', error);
    throw error;
  }
}

/**
 * Vérifie si une garantie est expirée
 */
export function isGarantieExpiree(dateExpiration: Timestamp | undefined): boolean {
  if (!dateExpiration) return true;
  return dateExpiration.toDate() < new Date();
}

/**
 * Calcule le nombre de jours restants avant expiration d'une garantie
 */
export function joursAvantExpiration(dateExpiration: Timestamp | undefined): number | null {
  if (!dateExpiration) return null;
  
  const now = new Date();
  const expiration = dateExpiration.toDate();
  const diffTime = expiration.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Retourne le statut des garanties pour une copropriété
 */
export function getStatutGaranties(copropriete: Copropriete): {
  gpa: { active: boolean; joursRestants: number | null };
  biennale: { active: boolean; joursRestants: number | null };
  decennale: { active: boolean; joursRestants: number | null };
} {
  return {
    gpa: {
      active: !isGarantieExpiree(copropriete.dateExpirationGPA),
      joursRestants: joursAvantExpiration(copropriete.dateExpirationGPA)
    },
    biennale: {
      active: !isGarantieExpiree(copropriete.dateExpirationBiennale),
      joursRestants: joursAvantExpiration(copropriete.dateExpirationBiennale)
    },
    decennale: {
      active: !isGarantieExpiree(copropriete.dateExpirationDecennale),
      joursRestants: joursAvantExpiration(copropriete.dateExpirationDecennale)
    }
  };
}

/**
 * Réinitialise le quota SENTINELLE (usage → 0, nouvelle date de renouvellement)
 * Session 81 : À utiliser lors du renouvellement annuel
 */
export async function resetQuotaSentinelle(coproprieteId: string): Promise<void> {
  try {
    const copropriete = await getCopropriete(coproprieteId);
    if (!copropriete) throw new Error('Copropriété non trouvée');

    const nouvelleDate = new Date();
    nouvelleDate.setFullYear(nouvelleDate.getFullYear() + 1);

    const docRef = doc(db, COLLECTION_NAME, coproprieteId);
    await updateDoc(docRef, {
      sentinelleUsage: 0,
      sentinelleRenouvellement: Timestamp.fromDate(nouvelleDate),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Erreur lors du reset du quota SENTINELLE:', error);
    throw error;
  }
}

/**
 * Ajoute des analyses supplémentaires au quota (achat de recharge)
 * Session 81 : Pour le bouton "+100 analyses"
 */
export async function ajouterQuotaSentinelle(
  coproprieteId: string,
  analysesSupplementaires: number
): Promise<void> {
  try {
    const copropriete = await getCopropriete(coproprieteId);
    if (!copropriete) throw new Error('Copropriété non trouvée');

    const nouveauQuota = (copropriete.sentinelleQuota || 0) + analysesSupplementaires;

    const docRef = doc(db, COLLECTION_NAME, coproprieteId);
    await updateDoc(docRef, {
      sentinelleQuota: nouveauQuota,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Erreur lors de l\'ajout au quota SENTINELLE:', error);
    throw error;
  }
}
